import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  OnInit,
  PLATFORM_ID,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { Store } from '@ngrx/store';
import {
  BehaviorSubject,
  combineLatest,
  fromEvent,
  Observable,
  startWith,
} from 'rxjs';
import { Booking, BookingStatus } from '../../../core/models/entities/booking';
import { BookingType } from '../../../core/enums/booking-type';
import { fromStore } from '../../../core/store';
import { bookingsActions } from '../../../core/store/bookings';
import { ModalService } from '../../../core/services/modal.service';
import { BookingDetailsModalComponent } from '../booking-details-modal/booking-details-modal.component';
import { DestroyableComponent } from '../../../shared/components/destroyable/destroyable.component';
import { map, take } from 'rxjs/operators';
import { selectAuthVendor } from '../../../core/store/reducers';
import { isPlatformBrowser } from '@angular/common';

@Component({
  selector: 'app-pending-bookings',
  template: `
    @if ((bookings$ | async)?.length > 0) {
      <div>
        <div class="mb-6 flex items-center">
          <div class="mr-4 text-xl font-bold">À confirmer</div>
          @if (loading$ | async) {
            <app-loader size="sm"></app-loader>
          } @else {
            <div
              class="flex h-8 w-8 items-center justify-center rounded-full bg-pink-500 text-white"
            >
              <div>{{ bookingsCount$ | async }}</div>
            </div>
          }
        </div>
        @if ((bookings$ | async)?.length > 0) {
          <div class="mb-4 flex gap-8" [class.flex-wrap]="seeAll$ | async">
            @for (booking of visibleBookings$ | async; track booking) {
              <div
                class="w-full flex-none"
                [style.max-width]="cardMaxWidth + 'px'"
              >
                <div class="relative flex h-full flex-col">
                  <app-card #pendingBooking data-cy="pendingBooking">
                    <div class="flex justify-between">
                      <div>
                        <div class="mb-1 font-medium">{{
                          booking.full_name
                        }}</div>
                        <div class="mb-1 text-gray-500">{{
                          booking.room.name
                        }}</div>
                      </div>
                      @if (vendor().slug === 'urbagestion') {
                        <app-badge variant="secondary">{{
                          booking.created_by_app ? 'App' : 'Web'
                        }}</app-badge>
                      }
                    </div>
                    @for (room of booking.complementary_rooms; track room) {
                      <div class="mb-1 text-gray-500">+ {{ room.name }}</div>
                    }
                    <div class="mb-4">
                      <app-booking-start-end
                        [booking]="booking"
                      ></app-booking-start-end>
                      @if (booking.booking_type === BookingType.MultipleDates && booking.siblings && booking.siblings.length > 1) {
                        <button
                          type="button"
                          class="mt-1 text-sm text-gray-400 hover:text-gray-600"
                          (click)="openBookingDetails(booking)"
                        >
                          + {{ booking.siblings.length - 1 }} date{{ booking.siblings.length > 2 ? 's' : '' }}
                        </button>
                      }
                    </div>
                    <div class="mt-auto flex justify-end gap-4">
                      <app-button
                        [attr.data-cy]="'open-booking-details'"
                        variant="primary"
                        size="sm"
                        (click)="openBookingDetails(booking)"
                        >Ouvrir la réservation
                      </app-button>
                    </div>
                    <div
                      class="absolute top-0 right-0 flex h-8 w-8 translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-pink-500 md:h-10 md:w-10"
                      ><i class="fal fa-bell text-white md:text-xl"></i
                    ></div>
                  </app-card>
                </div>
              </div>
            }
          </div>
        }
        @if (seeAllButtonVisible$ | async) {
          <button (click)="toggle()" class="pt-4 underline">{{
            (seeAll$ | async) === true ? 'Voir moins' : 'Voir tous'
          }}</button>
        }
        <app-spacer size="8"></app-spacer>
      </div>
    }
  `,
  standalone: false,
})
export class PendingBookingsComponent
  extends DestroyableComponent
  implements OnInit, AfterViewInit
{
  @ViewChildren('pendingBooking') pendingBooking: QueryList<ElementRef>;
  bookings$: Observable<Booking[]> = this.store.select(
    fromStore.selectAllPendingBookings,
  );
  visibleBookings$: Observable<Booking[]>;
  loading$: Observable<boolean> = this.store.select(
    fromStore.selectAllPendingBookingsLoading,
  );
  bookingsCount$: Observable<number> = this.store.select(
    fromStore.selectAllPendingBookingsCount,
  );
  calendarWidth$: Observable<number>;

  BookingStatus = BookingStatus;
  BookingType = BookingType;
  hasHiddenBookings: boolean;

  seeAll$ = new BehaviorSubject(false);
  hasHiddenBookings$: Observable<boolean>;
  seeAllButtonVisible$: Observable<boolean>;
  vendor = this.store.selectSignal(selectAuthVendor);

  cardMaxWidth = 352;
  platformId = inject(PLATFORM_ID);

  constructor(
    private store: Store,
    private modalService: ModalService,
    private elem: ElementRef,
    private cdr: ChangeDetectorRef,
  ) {
    super();
  }

  ngOnInit() {
    this.store.dispatch(bookingsActions.findAllPending());
  }

  ngAfterViewInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.calendarWidth$ = combineLatest([
        this.bookings$,
        fromEvent(window, 'resize'),
      ]).pipe(
        startWith(this.elem.nativeElement.offsetWidth ?? 0),
        map((ev) => {
          return this.elem.nativeElement.offsetWidth ?? 0;
        }),
      );

      this.visibleBookings$ = combineLatest([
        this.calendarWidth$,
        this.bookings$,
        this.seeAll$,
      ]).pipe(
        map(([width, bookings, seeAll]) => {
          if (seeAll) return bookings;
          return bookings.slice(
            0,
            Math.max(1, Math.trunc(width / (this.cardMaxWidth + 32))),
          );
        }),
      );

      this.hasHiddenBookings$ = combineLatest([
        this.bookings$,
        this.visibleBookings$,
      ]).pipe(
        map(
          ([bookings, visibleBookings]) =>
            bookings?.length > visibleBookings?.length,
        ),
      );

      this.seeAllButtonVisible$ = combineLatest([
        this.hasHiddenBookings$,
        this.seeAll$,
      ]).pipe(
        map(([hasHiddenBookings, seeAll]) => {
          return seeAll || (!seeAll && hasHiddenBookings);
        }),
      );

      this.cdr.detectChanges();
    }
  }

  openBookingDetails(booking: Booking) {
    const instance = this.modalService.open(BookingDetailsModalComponent, {});
    (instance.componentRef.instance as BookingDetailsModalComponent).bookingId =
      booking.id;
  }

  toggle() {
    this.seeAll$
      .pipe(take(1))
      .subscribe((seeAll) => this.seeAll$.next(!seeAll));
  }
}
